import React, { useState } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import HomeIcon from '@mui/icons-material/Home'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import AssignmentIcon from '@mui/icons-material/Assignment'
import PersonIcon from '@mui/icons-material/Person'
import MUIThemeProvider from './components/MUIThemeProvider'
import { AuthProvider, useAuth } from './components/auth/AuthContext'

// Import page components
import HomePage from './pages/HomePage'
import FAQPage from './pages/FAQPage'
import RegistrationPage from './pages/RegistrationPage'
import PasswordResetPage from './pages/PasswordResetPage'
import UserOrdersPage from './pages/user/OrdersPage'
import UserOrderDetailPage from './pages/user/OrderDetailPage'
import ProfilePage from './pages/user/ProfilePage'
import AdminPage from './pages/admin/AdminPage'
import AdminMessagesPage from './pages/admin/MessagesPage'
import AdminOrdersPage from './pages/admin/OrdersPage'
import AdminOrderPage from './pages/admin/OrderPage'
import AdminConferencesPage from './pages/admin/ConferencesPage'
import AdminConferenceDetailPage from './pages/admin/ConferenceDetailPage'
import AdminSummaryPage from './pages/admin/SummaryPage'
import AdminExportsPage from './pages/admin/ExportsPage'
import UsersPage from './pages/admin/UsersPage'
import ReportsPage from './pages/admin/ReportsPage'
import UserAvatarMenu from './components/auth/UserAvatarMenu'
import UtilitiesPage from './pages/admin/utilities/UtilitiesPage'
import AppSnackBar from './components/AppSnackBar'
import AppContext, { AppContextProvider } from './components/AppContext'

function AppMenu() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleHomeClick = () => {
    handleMenuClose();
    navigate('/');
  };

  const handleFAQClick = () => {
    handleMenuClose();
    navigate('/faq');
  };

  const handleAdminClick = () => {
    handleMenuClose();
    navigate('/admin');
  };

  const handleOrdersClick = () => {
    handleMenuClose();
    navigate('/user/orders');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/user/profile');
  };

  const showInternalLinks = currentUser && (currentUser.userType === 'internal' || currentUser.userType === 'admin');
  const showCustomerLinks = currentUser && currentUser.userType === 'user';

  return (
    <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 1000 }}>
      <IconButton
        onClick={handleMenuClick}
        size="small"
        sx={{ ml: 2 }}
        aria-controls={open ? 'app-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <MenuIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="app-menu"
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              left: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleHomeClick}>
          <ListItemIcon>
            <HomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Home</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleFAQClick}>
          <ListItemIcon>
            <QuestionAnswerIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>FAQ</ListItemText>
        </MenuItem>
        {showCustomerLinks && (
          <MenuItem onClick={handleOrdersClick}>
            <ListItemIcon>
              <AssignmentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My Orders</ListItemText>
          </MenuItem>
        )}
        {showCustomerLinks && (
          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>
        )}
        {showInternalLinks && (
          <MenuItem onClick={handleAdminClick}>
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Admin</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}


function App() {
  return (
    <div>
      <MUIThemeProvider>
        <AppContextProvider>
          <AuthProvider>
            <AppSnackBar />
            <AppMenu />
            <UserAvatarMenu />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/registration" element={<RegistrationPage />} />
              <Route path="/user/orders" element={<UserOrdersPage />} />
              <Route path="/user/order/:orderId" element={<UserOrderDetailPage />} />
              <Route path="/user/profile" element={<ProfilePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/orders" element={<AdminOrdersPage />} />
              <Route path="/admin/order/:orderId" element={<AdminOrderPage />} />
              <Route path="/admin/conferences" element={<AdminConferencesPage />} />
              <Route path="/admin/conferences/:id" element={<AdminConferenceDetailPage />} />
              <Route path="/admin/messages" element={<AdminMessagesPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/summary" element={<AdminSummaryPage />} />
              <Route path="/admin/exports" element={<AdminExportsPage />} />
              <Route path="/admin/utilities" element={<UtilitiesPage />} />
              <Route path="/admin/reports" element={<ReportsPage />} />
              <Route path="/reset-password" element={<PasswordResetPage />} />
            </Routes>
          </AuthProvider>
        </AppContextProvider>
      </MUIThemeProvider>
    </div>
  )
}

export default App